import { Component, OnInit } from '@angular/core';
import { FunctionalityDto } from '@models/authorizations/functionality.entity';
import { GroupDto } from '@models/authorizations/group.model';
import {
  OperationEnum,
  OperationString,
  Operations,
} from '@models/authorizations/operation.entity';
import { AuthorizationService } from '@services/authorization.service';
import { MessageService } from '@services/message.service';
import { isEmpty } from '@utils/is-empty.function';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, finalize, throwError } from 'rxjs';

@Component({
  selector: 'ebs-functionality-list-dialog',
  templateUrl: './functionality-list-dialog.component.html',
  styleUrls: ['./functionality-list-dialog.component.scss'],
})
export class FunctionalityListDialogComponent implements OnInit {
  public group!: GroupDto;

  public functionalitiesLoading = true;

  public operationShortString = OperationString;
  operationSelectionVisible: boolean = false;

  public operations: any[] = Object.keys(Operations).map((v: string) => {
    return {
      code: v,
      operation: this.getOperation(v),
    };
  });

  filteredOperations: any[] = [];
  selectedOperations: any[] | undefined = [];

  public get isChangeMode(): boolean {
    return !isEmpty(this.group);
  }

  public get selectedFunctionality(): FunctionalityDto[] {
    return !isEmpty(this.group)
      ? this.group.authorizations
          .map((elem) =>
            this.functionalities.find(
              (func) =>
                func.functionId === elem.functionality.functionId,
            ),
          )
          .filter((func): func is FunctionalityDto => func !== undefined)
      : [];
  }

  public search: string = '';
  public currentlyEditing: FunctionalityDto | null = null;
  public functionalities: FunctionalityDto[] = [];

  constructor(
    public ref: DynamicDialogRef,
    private messageService: MessageService,
    private authService: AuthorizationService,
    public config: DynamicDialogConfig,
  ) {
    this.group = this.config.data?.group ?? null;

    // Titolo nell'header nativo della DynamicDialog.
    this.config.header = this.isChangeMode
      ? 'Selezione autorizzazioni'
      : 'Gestione funzionalità';
  }

  ngOnInit(): void {
    this.loadFunctionalities();
  }

  loadFunctionalities(): void {
    this.functionalitiesLoading = true;

    this.authService
      .functionalityList()
      .pipe(
        catchError((err) => {
          this.messageService.error(
            'Gestione funzionalità',
            'Errore durante il caricamento delle funzionalità',
          );
          return throwError(() => err);
        }),
        finalize(() => {
          this.functionalitiesLoading = false;
        }),
      )
      .subscribe({
        next: (result: FunctionalityDto[]) => {
          this.functionalities = result;
        },
        error: () => {
          // Il caricamento viene comunque terminato da finalize.
        },
      });
  }

  createNewFunctionality(): void {
    this.functionalities.splice(0, 0, {
      description: '',
      name: '',
      visibleName: '',
      entity: '',
      isTransactional: false,
      path: '',
      functionalityRequiredOperations: [],
    });

    this.currentlyEditing = this.functionalities[0];
    this.selectedOperations = [];
  }

  onAllSelected(functionalities: FunctionalityDto[]): void {
    if (!this.isChangeMode) {
      return;
    }

    const newAuth = functionalities.filter(
      (elem) =>
        this.group.authorizations.findIndex(
          (func) =>
            elem.functionId === func.functionality.functionId,
        ) === -1,
    );

    const oldAuth = this.group.authorizations.filter(
      (elem) =>
        functionalities.findIndex(
          (func) =>
            elem.functionality.functionId === func.functionId,
        ) === -1,
    );

    newAuth.forEach((functionality) => {
      this.group.authorizations.splice(0, 0, {
        functionality,
        groupAllowedOperations: [],
        groupAllowedFields: [],
      });
    });

    oldAuth.forEach((auth) => {
      const index = this.group.authorizations.findIndex(
        (auth2) =>
          auth.functionality.functionId ===
          auth2.functionality.functionId,
      );

      if (index !== -1) {
        this.group.authorizations.splice(index, 1);
      }
    });
  }

  confirm(): void {
    this.ref.close(this.group.authorizations);
  }

  cancel(): void {
    this.ref.close(false);
  }

  removeFunction(
    functionality: FunctionalityDto,
    index: number,
  ): void {
    this.authService
      .removeFunctionality([functionality])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Rimozione funzione',
            'Errore durante la rimozione',
          );
          return throwError(() => err);
        }),
      )
      .subscribe({
        next: () => {
          this.currentlyEditing = null;
          this.editFunction(null, index);
          this.messageService.success(
            'Rimozione funzione',
            'Rimozione avvenuta con successo',
          );
        },
        error: () => {},
      });
  }

  editFunction(
    functionality: FunctionalityDto | null,
    index: number,
  ): void {
    if (
      isEmpty(functionality) &&
      isEmpty(this.currentlyEditing?.functionId)
    ) {
      this.functionalities.splice(index, 1);
    }

    this.currentlyEditing = functionality;

    if (functionality !== null) {
      this.selectedOperations = this.operations.filter((operation) =>
        functionality.functionalityRequiredOperations?.some(
          (required) =>
            required === operation.code ||
            (required as any)?.operation === operation.code,
        ),
      );
    } else {
      this.selectedOperations = [];
    }
  }

  modifyFunction(): void {
    if (this.currentlyEditing !== null) {
      this.saveFunctionChanges(this.currentlyEditing);
    }
  }

  saveFunctionChanges(functionality: FunctionalityDto): void {
    this.authService
      .modifyFunctionality([functionality])
      .pipe(
        catchError((err) => {
          this.currentlyEditing = null;
          this.messageService.error(
            'Modifica funzione',
            'Errore durante la modifica',
          );
          return throwError(() => err);
        }),
      )
      .subscribe({
        next: () => {
          this.currentlyEditing = null;
          this.messageService.success(
            'Modifica funzione',
            'Modifica avvenuta con successo',
          );
        },
        error: () => {},
      });
  }

  getOperation(operation: string): string {
    if (typeof operation === 'string') {
      switch (operation) {
        case 'create':
          return OperationString[OperationEnum.CREATE];
        case 'modify':
          return OperationString[OperationEnum.MODIFY];
        case 'read':
          return OperationString[OperationEnum.READ];
        case 'remove':
          return OperationString[OperationEnum.REMOVE];
      }
    }

    return '';
  }

  filterOperation(event: AutoCompleteCompleteEvent): void {
    const filtered: any[] = [];
    const query = event.query.toLowerCase();

    for (const operation of this.operations) {
      if (
        operation.operation.toLowerCase().indexOf(query) === 0
      ) {
        filtered.push(operation);
      }
    }

    this.filteredOperations = filtered;
  }
}
